import { z } from 'zod';
import { URLS } from '@/constants/urls';

// Helper to convert empty strings to undefined for Zod defaults
const emptyToUndefined = (val: string | undefined) => (val === '' ? undefined : val);

/**
 * Environment schema with validation rules
 * Portal URLs read from ports.json (single source of truth) - can be overridden by env vars
 * For local dev: uses ports.json defaults
 * For staging/production: override with env vars from domains.json
 */
const envSchema = z.object({
  // Firebase - REQUIRED for Auth Portal to function
  // For local dev without Firebase, use: ./scripts/start.sh dev:login
  // Use preprocess to handle empty strings from Docker builds
  VITE_FIREBASE_API_KEY: z.preprocess(emptyToUndefined, z.string().min(1, 'Firebase API key is required')),
  VITE_FIREBASE_AUTH_DOMAIN: z.preprocess(emptyToUndefined, z.string().min(1, 'Firebase auth domain is required')),
  VITE_FIREBASE_PROJECT_ID: z.preprocess(emptyToUndefined, z.string().min(1, 'Firebase project ID is required')),
  VITE_FIREBASE_STORAGE_BUCKET: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_FIREBASE_APP_ID: z.preprocess(emptyToUndefined, z.string().min(1, 'Firebase app ID is required')),

  // API URL
  // Local dev: reads from ports.json (URLS constant)
  // Production: requires env var (no fallback) to catch pipeline issues
  VITE_API_URL: import.meta.env.DEV
    ? z.preprocess(emptyToUndefined, z.string().url().optional()).transform((v) => v ?? URLS.API_GATEWAY)
    : z.preprocess(emptyToUndefined, z.string().url('API URL is required in production')),

  // Portal URLs
  // Local dev: reads from ports.json (URLS constants)
  // Production: requires env vars (no fallback) to catch pipeline issues
  VITE_ADMIN_PORTAL_URL: import.meta.env.DEV
    ? z.preprocess(emptyToUndefined, z.string().url().optional()).transform((v) => v ?? URLS.ADMIN_PORTAL)
    : z.preprocess(emptyToUndefined, z.string().url('Admin portal URL is required in production')),
  VITE_BUSINESS_PORTAL_URL: import.meta.env.DEV
    ? z.preprocess(emptyToUndefined, z.string().url().optional()).transform((v) => v ?? URLS.BUSINESS_PORTAL)
    : z.preprocess(emptyToUndefined, z.string().url('Business portal URL is required in production')),
  VITE_CONSUMER_PORTAL_URL: import.meta.env.DEV
    ? z.preprocess(emptyToUndefined, z.string().url().optional()).transform((v) => v ?? URLS.CONSUMER_PORTAL)
    : z.preprocess(emptyToUndefined, z.string().url('Consumer portal URL is required in production')),
  VITE_AUTH_PORTAL_URL: import.meta.env.DEV
    ? z.preprocess(emptyToUndefined, z.string().url().optional()).transform((v) => v ?? URLS.AUTH_PORTAL)
    : z.preprocess(emptyToUndefined, z.string().url('Auth portal URL is required in production')),

  // SSO SAML Provider ID — optional, only shown when configured
  VITE_SSO_PROVIDER_ID: z.string().optional(),

  // OpenTelemetry Browser Tracing (Issue #469)
  // Return undefined if not set (or empty string), so ?? operator works for default
  VITE_OTEL_ENABLED: z
    .string()
    .optional()
    .transform((val) => (val === undefined || val === '' ? undefined : val === 'true')),
  VITE_OTEL_COLLECTOR_URL: z.string().url().optional(),
  VITE_OTEL_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),
});

function validateEnv() {
  const parsed = envSchema.safeParse(import.meta.env);

  if (!parsed.success) {
    console.error('Invalid environment variables:', parsed.error.flatten().fieldErrors);
    throw new Error('Invalid environment variables');
  }

  return parsed.data;
}

export const env = validateEnv();

export default env;
